import { Plus } from '@emotion-icons/bootstrap';
import { ArrowDropDown } from '@emotion-icons/remix-line';
import { useState } from 'react';
import ClickOutside from '~/components/ClickOutside';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import TextButton from '~/components/TextButton';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import EditIcon from '~/icons/EditIcon';
import AddClientModal from '~/routes/User/Client/AddClient/AddClientModal';
import { IBaseSectionType } from '../types';
import * as Styles from './styles';
import useCustomerSection from './useCustomerSection';

const CustomerSection = ({ formData, setValue }: IBaseSectionType) => {
  const isDesktop = useIsDesktop();
  const [editingClientId, setEditingClientId] = useState<string | null>(null);

  const {
    open,
    setOpen,
    customers,
    isModalOpen,
    onCloseClientModal,
    onOpenClientModal,
    setCustomer,
  } = useCustomerSection(setValue, formData.client_uuid);

  const handleEditClient = (clientId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent dropdown item click
    setEditingClientId(clientId);
    onOpenClientModal(); // Use the same modal open function
    setOpen(false);
  };

  const handleCloseModal = () => {
    setEditingClientId(null);
    onCloseClientModal();
  };

  const handleClientCreatedOrUpdated = (client: any) => {
    if (editingClientId) {
      // Editing mode: update selection if the edited client is currently selected
      if (client.business_client_uuid === formData.client_uuid) {
        setCustomer(client);
      }
    } else {
      // Creating mode: auto-select the newly created client
      setCustomer(client);
    }
    setEditingClientId(null);
  };

  const selectedClient = customers.find(
    customer => customer.business_client_uuid === formData.client_uuid,
  );

  return (
    <div>
      <Text size={isDesktop ? 'md' : 'ssmd'} className={isDesktop ? 'bold' : ''}>
        Client Details
      </Text>
      <Gap t={isDesktop ? 32 : 16} />

      <AddClientModal
        isModalOpen={isModalOpen}
        onCloseClientModal={handleCloseModal}
        onClientCreated={handleClientCreatedOrUpdated}
        heading={editingClientId ? 'Edit Client' : 'Add New Client'}
        clientId={editingClientId}
      />

      <div>
        <ClickOutside onClose={() => setOpen(false)}>
          <Styles.DropdownWrapper>
            <Styles.StyledDropdown
              onClick={() => setOpen(!open)}
              className={selectedClient ? '' : 'error-field'}
            >
              {selectedClient ? selectedClient.company_name : 'Select a client'}
              <Styles.DropdownIcon open={open}>
                <ArrowDropDown size={30} />
              </Styles.DropdownIcon>
            </Styles.StyledDropdown>

            {open && (
              <Styles.DropdownContainer>
                <Styles.CreateButtonContainer
                  className="cursor-pointer align-center"
                  onClick={() => {
                    onOpenClientModal();
                    setOpen(false);
                  }}
                >
                  <Styles.PlusWrapper className="center">
                    <Plus className="bold" size={24} />
                  </Styles.PlusWrapper>
                  <Text variant="themed" size="sm" className="semi-bold">
                    Create new client
                  </Text>
                </Styles.CreateButtonContainer>
                <Styles.DropdownList>
                  {customers.map(customer => (
                    <Styles.DropdownItem
                      key={customer.business_client_uuid}
                      onClick={() => {
                        setCustomer(customer);
                      }}
                    >
                      <Styles.DropdownItemContent>
                        <Text size="sm" className="semi-bold">
                          {customer.company_name}
                        </Text>
                        <Styles.CustomerDetails>
                          {customer.first_name} {customer.last_name}
                          <br />
                          {customer.email}
                        </Styles.CustomerDetails>
                      </Styles.DropdownItemContent>
                      <Styles.EditButton
                        onClick={event => handleEditClient(customer.business_client_uuid, event)}
                        title="Edit client"
                      >
                        <EditIcon />
                      </Styles.EditButton>
                    </Styles.DropdownItem>
                  ))}
                </Styles.DropdownList>
              </Styles.DropdownContainer>
            )}
          </Styles.DropdownWrapper>
        </ClickOutside>
      </div>

      {selectedClient && (
        <Styles.SelectedClientWrapper>
          <div>
            <Text size="xs" variant="secondary">
              {selectedClient.address_line_1}
            </Text>
            <Text size="xs" variant="secondary">
              {selectedClient.address_line_2}
            </Text>
            <Text size="xs" variant="secondary">
              {selectedClient.city}
            </Text>
            <Text size="xs" variant="secondary">
              {selectedClient.state}
            </Text>
            <Text size="xs" variant="secondary">
              {selectedClient.country}
            </Text>
            {selectedClient.email && (
              <Text size="xs" variant="secondary">
                Email: {selectedClient.email}
              </Text>
            )}
          </div>
          <div>
            <TextButton
              onClick={event => handleEditClient(selectedClient.business_client_uuid, event)}
            >
              View/Edit Details
            </TextButton>
          </div>
        </Styles.SelectedClientWrapper>
      )}
    </div>
  );
};

export default CustomerSection;
